import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { MessageSquare, ArrowLeft, RefreshCw, Home } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface EmptyChatStateProps {
  type: 'empty-history' | 'load-error' | 'network-error';
  subjectName?: string;
  chatId?: string;
  onRetry?: () => void;
}

export function EmptyChatState({ type, subjectName, chatId, onRetry }: EmptyChatStateProps) {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/dashboard');
  };

  const handleRefresh = () => {
    if (onRetry) {
      onRetry();
    } else {
      window.location.reload();
    }
  };

  const getContent = () => {
    switch (type) {
      case 'empty-history':
        return {
          icon: <MessageSquare className="h-12 w-12 text-muted-foreground" />,
          title: 'Empty Chat History',
          description: `This ${subjectName ? `${subjectName} ` : ''}chat doesn't have any messages yet. The conversation history appears to be empty, but you can start chatting now!`,
          suggestions: [
            'Ask a question to get started',
            'Use the input box below to begin learning',
            `Explore ${subjectName || 'the subject'} concepts`
          ]
        };
      
      case 'load-error':
        return {
          icon: <MessageSquare className="h-12 w-12 text-destructive" />,
          title: 'Failed to Load Chat',
          description: 'We encountered an issue loading this chat conversation. This might be temporary.',
          suggestions: [
            'Check your internet connection',
            'Try refreshing the page',
            'Go back to dashboard and try again'
          ]
        };
      
      case 'network-error':
        return {
          icon: <MessageSquare className="h-12 w-12 text-orange-500" />,
          title: 'Connection Issue',
          description: 'Unable to connect to the server. Please check your network connection.',
          suggestions: [
            'Check your internet connection',
            'Wait a moment and try again',
            'Contact support if the issue persists'
          ]
        };
      
      default:
        return {
          icon: <MessageSquare className="h-12 w-12 text-muted-foreground" />,
          title: 'Chat Unavailable',
          description: 'This chat is currently unavailable.',
          suggestions: ['Try going back to the dashboard']
        };
    }
  };

  const content = getContent();

  return (
    <div className="flex items-center justify-center min-h-[400px] p-6">
      <Card className="w-full max-w-md">
        <CardContent className="p-8 text-center space-y-6">
          <div className="flex justify-center">
            {content.icon}
          </div>
          
          <div className="space-y-2">
            <h3 className="text-lg font-semibold">{content.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {content.description}
            </p>
          </div>

          {content.suggestions.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground">Suggestions:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                {content.suggestions.map((suggestion, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-primary">•</span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex flex-col gap-2 pt-4">
            {onRetry && (
              <Button onClick={handleRefresh} variant="default" size="sm">
                <RefreshCw className="mr-2 h-4 w-4" />
                Try Again
              </Button>
            )}
            <Button onClick={handleGoHome} variant="outline" size="sm">
              <Home className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </div>

          {chatId && type !== 'empty-history' && (
            <p className="text-xs text-muted-foreground/60 mt-4">
              Chat ID: {chatId}
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
